import { ReactElement, useMemo, useState } from 'react'
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { IfcPropertiesView } from '@editor/components/IFCElementForm/components'
import IFCMetadataAssignmentsSummary from '@editor/components/IFCMetadataAssignmentsSummary'
import { CorneredSection } from '@ui2/structure'
import useIFCElementSelection from '../../hooks/useIFCElementSelection'
import IFCFilterButton from '../IFCFilterButton'
import IFCMetadataPendingNotice from '../IFCMetadataPendingNotice'
import IFCSelectionInfo from '../IFCSelectionInfo'

/** What the filter action applies to: the selected elements, or everything except them. */
type IFCFilterSelectionMode = 'selected' | 'except-selected'

/**
 * Filter mode for the IFC Import page: select IFC elements in the scene (click or marquee) and add
 * them to the model's filtered elements — either the selected elements themselves ("Ausgewählte")
 * or all remaining elements except them ("Außer ausgewählte"). Shows the shared "Gefilterte
 * Elemente" list.
 */
const IFCFilterPanel = (): ReactElement => {
  const { selectedIfcElementGuids, setSelectedIfcElementGuids, metadata, updateIfcModelMetadata } =
    useIFCElementSelection()

  const [selectionMode, setSelectionMode] = useState<IFCFilterSelectionMode>('selected')

  const filteredElements = metadata?.filteredElements ?? []

  // "Außer ausgewählte" filters every not-yet-filtered element except the selected ones. Both
  // modes require a selection so an empty selection can never filter the whole model.
  const exceptSelectedGuids = useMemo(() => {
    const alreadyFilteredGuids = new Set((metadata?.filteredElements ?? []).flat())
    const selectedGuids = new Set(selectedIfcElementGuids)
    return Object.keys(metadata?.property_data?.data ?? {}).filter(
      guid => !alreadyFilteredGuids.has(guid) && !selectedGuids.has(guid),
    )
  }, [metadata, selectedIfcElementGuids])

  if (!metadata)
    return (
      <CorneredSection>
        <IFCMetadataPendingNotice />
      </CorneredSection>
    )

  const guidsToFilter = selectionMode === 'selected' ? selectedIfcElementGuids : exceptSelectedGuids

  const removeFilteredEntry = (indexToRemove: number) => {
    updateIfcModelMetadata(current => {
      if (!current) return current
      return {
        ...current,
        filteredElements: (current.filteredElements ?? []).filter(
          (_item, index) => index !== indexToRemove,
        ),
      }
    })
  }

  return (
    <Box>
      <Stack spacing={2}>
        <CorneredSection title="Auswahl">
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {selectionMode === 'selected'
                ? 'Elemente per Klick oder Rahmenauswahl markieren und anschließend filtern.'
                : 'Elemente per Klick oder Rahmenauswahl markieren; gefiltert werden alle übrigen Elemente.'}
            </Typography>

            <IFCSelectionInfo showWhenEmpty />

            <ToggleButtonGroup
              size="small"
              exclusive
              fullWidth
              value={selectionMode}
              onChange={(_event, value: IFCFilterSelectionMode | null) =>
                value && setSelectionMode(value)
              }
              sx={{ '& .MuiToggleButton-root': { py: 0.25 } }}
            >
              <ToggleButton value="selected" data-cy="btn-ifc-filter-mode-selected">
                Ausgewählte
              </ToggleButton>
              <ToggleButton value="except-selected" data-cy="btn-ifc-filter-mode-except-selected">
                Außer ausgewählte
              </ToggleButton>
            </ToggleButtonGroup>

            <IFCFilterButton
              guids={guidsToFilter}
              disabled={selectedIfcElementGuids.length === 0}
              updateIfcModelMetadata={updateIfcModelMetadata}
              onFiltered={() => setSelectedIfcElementGuids([])}
              enableSpaceHotkey
              dataCy="btn-ifc-filter"
            />
          </Stack>
        </CorneredSection>

        <IFCMetadataAssignmentsSummary
          filteredElements={filteredElements}
          elementTypeAssignments={[]}
          storeyAssignments={[]}
          onRemoveFilteredEntry={removeFilteredEntry}
        />

        {selectedIfcElementGuids.length > 0 && (
          <>
            <CorneredSection title="Eigenschaften" collapsible defaultOpen>
              <IfcPropertiesView
                guids={selectedIfcElementGuids}
                metadata={metadata}
                onSelectGuids={setSelectedIfcElementGuids}
              />
            </CorneredSection>
          </>
        )}
      </Stack>
    </Box>
  )
}

export default IFCFilterPanel
